if (!customElements.get('optional-file-input')) {
  customElements.define(
    'optional-file-input',
    class OptionalFileInput extends HTMLElement {
      constructor() {
        super()
        this.showOnYes = this.dataset.showOnYes === 'true'
        this.sectionId = this.dataset.sectionId
        this.productFormId = this.dataset.productFormId || ''
        this.buttonTextNoFile = this.dataset.buttonTextNoFile || 'Upload Files Above'
        this.buttonTextNoPreviousOrder = this.dataset.buttonTextNoPreviousOrder || 'Enter order number above'
        this.fileButtonText = this.dataset.fileButtonText || 'Upload'
        this.showPreviousOrderTab = this.dataset.showPreviousOrderTab === 'true'
        this.selectedFiles = []
        this.thumbnailUrls = []
        this.boundFormSubmit = this.onFormSubmit.bind(this)
      }

      connectedCallback() {
        this.initToggle()
        this.initTabs()
        this.initFileInput()
        this.initPreviousOrder()

        this.realSubmitButton = this.sectionId
          ? document.getElementById(`ProductSubmitButton-${this.sectionId}`)
          : null
        if (this.realSubmitButton) {
          this.createCloneButton()
        }

        document.addEventListener('submit', this.boundFormSubmit, true)
        this.cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (evt) => {
          if (evt.source === 'product-form') this.reset()
        })

        const checkedInput = this.querySelector('[data-toggle-input]:checked')
        if (checkedInput) {
          this.handleToggleChange(checkedInput)
        }
      }

      createCloneButton() {
        const real = this.realSubmitButton
        this.fakeButton = document.createElement('button')
        this.fakeButton.type = 'button'
        this.fakeButton.className = real.className
        this.fakeButton.setAttribute('data-optional-file-trigger', '')
        this.fakeButton.innerHTML = `<span>${this.escapeHtml(this.buttonTextNoFile)}</span>`
        this.fakeButton.style.display = 'none'
        this.fakeButton.addEventListener('click', (e) => {
          e.preventDefault()
          this.scrollToUpload()
        })
        real.parentNode.insertBefore(this.fakeButton, real.nextSibling)
      }

      escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
      }

      scrollToUpload() {
        const wrapper = this.querySelector('[data-upload-wrapper]')
        if (wrapper) {
          wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.boundFormSubmit, true)
        if (this.cartUpdateUnsub) this.cartUpdateUnsub()
        if (this.fakeButton && this.fakeButton.parentNode) {
          this.fakeButton.parentNode.removeChild(this.fakeButton)
        }
        if (this.realSubmitButton) {
          this.realSubmitButton.style.display = ''
        }
      }

      /* ========== Toggle Yes/No ========== */

      initToggle() {
        this.toggleInputs = this.querySelectorAll('[data-toggle-input]')
        this.uploadWrapper = this.querySelector('[data-upload-wrapper]')
        this.fileInput = this.querySelector('[data-file-input]')

        this.toggleInputs.forEach((input) => {
          input.addEventListener('change', () => this.handleToggleChange(input))
        })
      }

      handleToggleChange(input) {
        const isYes = input.value === 'yes'
        const shouldShow = this.showOnYes ? isYes : !isYes

        if (shouldShow) {
          this.uploadWrapper.hidden = false
          if (this.activeTab === 'upload' && this.fileInput) this.fileInput.disabled = false
        } else {
          this.uploadWrapper.hidden = true
          if (this.fileInput) this.fileInput.disabled = true
          this.clearFiles()
          this.clearPreviousOrder()
          this.setActiveTab('upload')
        }

        this.updateButtonVisibility()
      }

      /* ========== Tabs (Upload / Send via E-Mail) ========== */

      initTabs() {
        this.tabButtons = this.querySelectorAll('[data-optional-file-tab]')
        this.panels = this.querySelectorAll('[data-optional-file-panel]')
        this.activeTab = 'upload'

        this.tabButtons.forEach((btn) => {
          btn.addEventListener('click', () => {
            const tab = btn.getAttribute('data-optional-file-tab')
            if (tab === 'upload' || tab === 'email' || tab === 'previous-order') {
              this.setActiveTab(tab)
            }
          })
        })
      }

      setActiveTab(tab) {
        this.activeTab = tab
        this.tabButtons?.forEach((btn) => {
          const isActive = btn.getAttribute('data-optional-file-tab') === tab
          btn.classList.toggle('optional-file-input__tab--active', isActive)
          btn.setAttribute('aria-selected', isActive)
        })
        this.panels?.forEach((panel) => {
          const match = panel.getAttribute('data-optional-file-panel') === tab
          panel.hidden = !match
          panel.classList.toggle('hidden', !match)
        })

        this.syncFileInputActive()
        this.syncPreviousOrderActive()
        this.updateFakeButtonText()
        this.updateButtonVisibility()
      }

      /** File input is only active/enabled when the Upload tab is selected AND the upload wrapper is visible. */
      syncFileInputActive() {
        if (!this.fileInput) return
        const shouldBeActive = this.activeTab === 'upload' && this.isUploadVisible()
        this.fileInput.disabled = !shouldBeActive
      }

      /* ========== Previous Order Tab ========== */

      initPreviousOrder() {
        this.prevOrderPanel = this.querySelector('[data-optional-file-panel="previous-order"]')
        this.prevOrderInput = this.querySelector('[data-previous-order-input]')
        this.prevOrderStatus = this.querySelector('[data-previous-order-status]')
        this.prevOrderTick = this.querySelector('[data-previous-order-tick]')
        this.prevOrderProperty = this.querySelector('[data-previous-order-property]')
        this.prevOrderRegex = null
        this.prevOrderErrorText = ''
        this.prevOrderHintText = ''

        if (!this.prevOrderPanel || !this.prevOrderInput) return

        const rawPattern = this.prevOrderPanel.dataset.previousOrderPattern || '^MZ\\d{5,8}$'
        this.prevOrderErrorText = this.prevOrderPanel.dataset.previousOrderError || 'Invalid order number.'
        this.prevOrderHintText = this.prevOrderPanel.dataset.previousOrderHint || ''
        try {
          this.prevOrderRegex = new RegExp(rawPattern)
        } catch (err) {
          console.warn('optional-file-input: invalid previous order regex, using fallback', err)
          this.prevOrderRegex = /^MZ\d{5,8}$/
        }

        this.prevOrderInput.addEventListener('input', (e) => {
          const upper = e.target.value.toUpperCase()
          if (upper !== e.target.value) e.target.value = upper
          this.validatePreviousOrder()
          this.updateButtonVisibility()
        })
        this.prevOrderInput.addEventListener('blur', () => {
          this.validatePreviousOrder()
          this.updateButtonVisibility()
        })
      }

      /** Validates the current input against the configured regex and updates status UI + hidden property. */
      validatePreviousOrder() {
        if (!this.prevOrderInput || !this.prevOrderRegex) return false
        const val = this.prevOrderInput.value.trim()
        const tick = this.prevOrderTick
        const property = this.prevOrderProperty

        if (!val) {
          this.setPreviousOrderStatus(this.prevOrderHintText, 'is-hint')
          if (tick) tick.hidden = true
          if (property) {
            property.value = ''
            property.disabled = true
          }
          return false
        }

        if (this.prevOrderRegex.test(val)) {
          this.setPreviousOrderStatus('', '')
          if (tick) tick.hidden = false
          if (property) {
            property.value = val
            property.disabled = false
          }
          return true
        }

        this.setPreviousOrderStatus(this.prevOrderErrorText, 'is-invalid')
        if (tick) tick.hidden = true
        if (property) {
          property.value = ''
          property.disabled = true
        }
        return false
      }

      /** Writes the status line: neutral hint, red error, or cleared when valid. */
      setPreviousOrderStatus(text, stateClass) {
        const status = this.prevOrderStatus
        if (!status) return
        status.textContent = text
        status.className = stateClass
          ? `optional-file-input__prev-order-status ${stateClass}`
          : 'optional-file-input__prev-order-status'
      }

      /** Keeps the hidden property input enabled only when the tab is active AND input is valid. */
      syncPreviousOrderActive() {
        if (!this.prevOrderProperty) return
        if (this.activeTab === 'previous-order') {
          this.validatePreviousOrder()
        } else {
          this.prevOrderProperty.value = ''
          this.prevOrderProperty.disabled = true
        }
      }

      hasValidPreviousOrder() {
        if (!this.prevOrderInput || !this.prevOrderRegex) return false
        return this.prevOrderRegex.test(this.prevOrderInput.value.trim())
      }

      isPreviousOrderSelected() {
        return this.activeTab === 'previous-order'
      }

      clearPreviousOrder() {
        if (this.prevOrderInput) this.prevOrderInput.value = ''
        this.setPreviousOrderStatus('', '')
        if (this.prevOrderTick) this.prevOrderTick.hidden = true
        if (this.prevOrderProperty) {
          this.prevOrderProperty.value = ''
          this.prevOrderProperty.disabled = true
        }
      }

      /* ========== File Input ========== */

      initFileInput() {
        this.fileInput = this.querySelector('[data-file-input]')
        this.thumbnailsContainer = this.querySelector('[data-thumbnails]')
        if (this.fileInput) {
          this.fileInput.addEventListener('change', () => this.onFilesSelected())
        }
      }

      onFilesSelected() {
        const newFiles = Array.from(this.fileInput.files || [])
        if (newFiles.length === 0) return
        this.selectedFiles = [...this.selectedFiles, ...newFiles]
        this.syncFilesToInput()
        this.renderThumbnails()
        this.updateButtonVisibility()
      }

      syncFilesToInput() {
        const dt = new DataTransfer()
        this.selectedFiles.forEach((f) => dt.items.add(f))
        this.fileInput.files = dt.files
        this.syncHiddenFileInputs()
      }

      /** Sync selectedFiles to hidden inputs with File 1, File 2, etc. for form submission */
      syncHiddenFileInputs() {
        const container = this.querySelector('[data-hidden-file-inputs]')
        const form = this.productFormId ? document.getElementById(this.productFormId) : null
        if (!container || !form) return
        container.innerHTML = ''
        this.selectedFiles.forEach((file, i) => {
          const propName = `properties[File ${i + 1}]`
          const input = document.createElement('input')
          input.type = 'file'
          input.name = propName
          input.setAttribute('form', this.productFormId)
          input.style.display = 'none'
          const dt = new DataTransfer()
          dt.items.add(file)
          input.files = dt.files
          container.appendChild(input)
        })
      }

      removeFile(index) {
        this.revokeThumbnailUrl(index)
        this.selectedFiles.splice(index, 1)
        this.thumbnailUrls.splice(index, 1)
        this.syncFilesToInput()
        this.renderThumbnails()
        this.updateButtonVisibility()
      }

      revokeThumbnailUrl(index) {
        if (this.thumbnailUrls[index]) {
          URL.revokeObjectURL(this.thumbnailUrls[index])
        }
      }

      renderThumbnails() {
        if (!this.thumbnailsContainer) return
        this.thumbnailUrls.forEach((url) => URL.revokeObjectURL(url))
        this.thumbnailUrls = []
        this.thumbnailsContainer.innerHTML = ''
        this.selectedFiles.forEach((file, index) => {
          const isImage = file.type.startsWith('image/')
          const el = document.createElement('div')
          el.className = 'optional-file-input__thumbnail' + (isImage ? '' : ' optional-file-input__thumbnail--pdf')
          el.setAttribute('role', 'listitem')
          if (isImage) {
            const url = URL.createObjectURL(file)
            this.thumbnailUrls.push(url)
            const img = document.createElement('img')
            img.className = 'optional-file-input__thumbnail-img'
            img.src = url
            img.alt = file.name
            el.appendChild(img)
          } else {
            el.innerHTML = `<span class="optional-file-input__thumbnail-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg></span>`
          }
          const removeBtn = document.createElement('button')
          removeBtn.type = 'button'
          removeBtn.className = 'optional-file-input__thumbnail-remove'
          removeBtn.setAttribute('aria-label', 'Remove file')
          removeBtn.innerHTML = '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 1l10 10M11 1L1 11"/></svg>'
          removeBtn.addEventListener('click', () => this.removeFile(index))
          el.appendChild(removeBtn)
          this.thumbnailsContainer.appendChild(el)
        })
      }

      updateFileButtonText() {
        const btn = this.querySelector('[data-file-button]')
        if (!btn) return
        btn.textContent = this.fileButtonText
      }

      clearFiles() {
        this.selectedFiles = []
        this.thumbnailUrls.forEach((url) => URL.revokeObjectURL(url))
        this.thumbnailUrls = []
        if (this.fileInput) this.fileInput.value = ''
        this.syncHiddenFileInputs()
        this.renderThumbnails()
        this.updateFileButtonText()
      }

      /** Reset to default state (no values retained). Call after successful add to cart. */
      reset() {
        const defaultYes = this.dataset.defaultYes === 'true'
        const noInput = this.querySelector('input[value="no"]')
        const yesInput = this.querySelector('input[value="yes"]')
        if (noInput && yesInput) {
          noInput.checked = !defaultYes
          yesInput.checked = defaultYes
        }
        this.uploadWrapper = this.querySelector('[data-upload-wrapper]')
        this.fileInput = this.querySelector('[data-file-input]')
        this.thumbnailsContainer = this.querySelector('[data-thumbnails]')
        const isYes = defaultYes
        const shouldShowUpload = this.showOnYes ? isYes : !isYes
        this.clearFiles()
        this.clearPreviousOrder()
        if (this.fileInput) this.fileInput.disabled = !shouldShowUpload
        this.setActiveTab('upload')
        if (this.uploadWrapper) {
          this.uploadWrapper.hidden = !shouldShowUpload
        }
        this.updateButtonVisibility()
      }

      hasFile() {
        return this.fileInput && this.fileInput.files && this.fileInput.files.length > 0
      }

      isUploadVisible() {
        const isYes = this.isYesSelected()
        return this.showOnYes ? isYes : !isYes
      }

      isSendViaEmailSelected() {
        return this.activeTab === 'email'
      }

      shouldShowFakeButton() {
        if (!this.realSubmitButton || !this.fakeButton) return false
        if (!this.isUploadVisible()) return false
        if (this.isSendViaEmailSelected()) return false
        if (this.isPreviousOrderSelected()) return !this.hasValidPreviousOrder()
        return !this.hasFile()
      }

      /** Fake button label depends on which tab is active and is blocking submit. */
      updateFakeButtonText() {
        if (!this.fakeButton) return
        const label = this.isPreviousOrderSelected() ? this.buttonTextNoPreviousOrder : this.buttonTextNoFile
        this.fakeButton.innerHTML = `<span>${this.escapeHtml(label)}</span>`
      }

      updateButtonVisibility() {
        if (!this.realSubmitButton || !this.fakeButton) return
        const section = this.closest('section')
        if (this.shouldShowFakeButton()) {
          this.updateFakeButtonText()
          this.realSubmitButton.style.display = 'none'
          this.fakeButton.style.display = ''
          const engravingClone = section?.querySelector('[data-engraving-trigger]')
          if (engravingClone) engravingClone.style.display = 'none'
        } else {
          this.realSubmitButton.style.display = ''
          this.fakeButton.style.display = 'none'
          section?.dispatchEvent(new CustomEvent('optional-file-upload-button-updated'))
        }
      }

      /* ========== Cart Integration ========== */

      getLinkedProductId() {
        const input = this.querySelector('[data-linked-product-id]')
        return input?.value || null
      }

      isYesSelected() {
        const yesInput = this.querySelector('input[value="yes"]')
        return yesInput?.checked || false
      }

      onFormSubmit(e) {
        const form = e.target
        if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return

        const section = this.closest('section')
        if (!section || !section.contains(form)) return

        const validation = CartAdd.validateInputModal(section, form)
        if (!validation.valid) {
          e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation()
          validation.inputModal.openAndShowErrors(validation.invalidFields)
          return
        }

        if (!this.isYesSelected()) return

        const linkedProductId = this.getLinkedProductId()
        if (!linkedProductId) return

        if (this.shouldShowFakeButton()) {
          e.preventDefault(); e.stopPropagation()
          this.scrollToUpload()
          return
        }

        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()

        const productFormEl = form.closest('product-form')
        const submitButton = productFormEl?.querySelector('[type="submit"]')
        const cart = CartAdd.getCart()

        CartAdd.setButtonLoading(submitButton, productFormEl, true)

        const formData = new FormData(form)
        const mainVariantId = formData.get('id')
        const quantity = parseInt(formData.get('quantity') || '1', 10)

        const { sections, sectionsUrl } = CartAdd.getSectionsConfig(cart)
        if (cart) cart.setActiveElement(document.activeElement)

        const finish = (response) => {
          CartAdd.handleResponse(response, {
            cart, productFormEl, mainVariantId,
            onSuccess: () => this.reset(),
          })
        }
        const finallyCb = () => CartAdd.setButtonLoading(submitButton, productFormEl, false)

        if (this.hasFile()) {
          const engravingNo = CartAdd.isEngravingNo(section)
          const addFormData = new FormData()
          for (const [key, value] of new FormData(form).entries()) {
            if (key.startsWith('properties[') && key.includes('Engraving -') && engravingNo)
              continue
            addFormData.append(key, value)
          }
          addFormData.append('sections', sections.join(','))
          addFormData.append('sections_url', sectionsUrl)
          CartAdd.postFormData(addFormData)
            .then(response => {
              if (response.status) { finish(response); return }
              const extraItems = [{ id: parseInt(linkedProductId, 10), quantity: 1 }]
              CartAdd.getUpsellItems(section).forEach(item => extraItems.push(item))
              return CartAdd.postJSON(extraItems, sections, sectionsUrl)
            })
            .then(linked => { if (linked) finish(linked) })
            .catch(console.error)
            .finally(finallyCb)
          return
        }

        const properties = CartAdd.buildMainProperties(formData, {
          section,
          skipEngravingWhenNo: true,
          skipFiles: false,
          skipVariantProp: false,
        })
        const mainItem = { id: parseInt(mainVariantId, 10), quantity }
        if (Object.keys(properties).length > 0) mainItem.properties = properties

        const items = [{ id: parseInt(linkedProductId, 10), quantity: 1 }]
        CartAdd.getUpsellItems(section).forEach(item => items.push(item))
        items.push(mainItem)

        CartAdd.postJSON(items, sections, sectionsUrl)
          .then(finish)
          .catch(console.error)
          .finally(finallyCb)
      }
    }
  )
}
